import React, { useEffect } from 'react';

import {
  Column,
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { TableOptions, usePagination, useTable } from 'react-table';

interface IProps {
  loading?: boolean;
  tableData: any[];
  tableColumn?: any;
  text?: string;
  pagination?: boolean;
  onSelectRow?: (row: any) => void;
  selectedRows?: Set<number>;
}

const InvenTable: React.FC<IProps> = ({ tableData }) => {
  const columns = React.useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'name',
        id: 'name',
        header: ({ table }) => <>Item</>,
        cell: ({ row, getValue }) => (
          <div
            style={{
              paddingLeft: `${row.depth * 2}rem`,
            }}
          >
            <div>
              {row.getCanExpand() ? (
                <button
                  {...{
                    onClick: row.getToggleExpandedHandler(),
                    style: { cursor: 'pointer' },
                  }}
                >
                  {row.getIsExpanded() ? '👇' : '👉'}
                </button>
              ) : (
                ''
              )}{' '}
              {getValue<boolean>()}
            </div>
          </div>
        ),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.inStock,
        id: 'inStock',
        cell: (info) => info.getValue(),
        header: () => <span>In Stock</span>,
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.lowStock,
        id: 'lowStock',
        cell: (info) => info.getValue(),
        header: () => <span>Low Stock</span>,
        footer: (props) => props.column.id,
      },
    ],
    []
  );

  const [data, setData] = React.useState<any[]>([]);

  const {
    getTableProps,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  }: any = useTable(
    {
      columns,
      data,
      autoResetPage: false,
    } as TableOptions<any>,
    usePagination
  );

  useEffect(() => {
    const makeDataLevel = tableData.map((x: any) => ({
      ...x,
      subRows: x.hasVariants ? x.variants : [],
      inStock: x.hasVariants ? null : x.variants?.[0]?.inStock,
      lowStock: x.hasVariants ? null : x.variants?.[0]?.lowStock,
    }));
    setData(makeDataLevel);
  }, [tableData]);

  const [expanded, setExpanded] = React.useState<ExpandedState>({});

  const table = useReactTable({
    data,
    columns,
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  return (
    <div className="p-2">
      <div className="h-2" />
      <div className="mt-5 rounded-xl border border-border-color-dark overflow-x-hidden relative  w-full ">
        <table
          {...getTableProps()}
          className="w-full text-left text-text-black "
        >
          <thead className="font-semibold text-black border-b border-border-color-dark">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <th
                      className="py-3 px-4 text-sm whitespace-nowrap"
                      style={{ width: 250 }}
                      key={header.id}
                      colSpan={header.colSpan}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => {
              return (
                <tr
                  className="bg-white border-b border-border-color-dark hover:bg-light-grey dark:hover:bg-light-grey dark:hover:bg-opacity-20 transition-all duration-700 ease-out cursor-pointer  text-base"
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td className="py-3 px-4 " key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {
        <div className=" bg-white p-5 flex justify-between ">
          <div className="flex items-center space-x-5">
            <div>
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className="bg-almost-white rounded-md h-6 w-6 pl-1 text-normal  text-black"
              >
                <MdKeyboardArrowLeft />
              </button>
              <button
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className="bg-almost-white rounded-md h-6 w-6 pl-1  text-normal  ml-3 text-black"
              >
                <MdKeyboardArrowRight />
              </button>
              <span className="ml-5 text-black  ">
                Page {pageIndex + 1} of {pageOptions.length}
              </span>
            </div>
          </div>
          <div className="text-black">
            <span className="text-sm text-text-black">Results per page</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
              className="font-normal px-1  text-sm rounded py-1 focus:outline-0 ml-2 bg-[white] border border-border-color-dark "
            >
              {[1, 5, 10, 20, 30, 40, 50].map((pageSize) => (
                <option key={pageSize} value={pageSize} className="text-sm">
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
        </div>
      }
    </div>
  );
};

function Filter({
  column,
  table,
}: {
  column: Column<any, any>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  return typeof firstValue === 'number' ? (
    <div className="flex space-x-2"></div>
  ) : null;
}

export default React.memo(InvenTable);
